import { useQuery } from "@tanstack/react-query";
import { useSupabaseConnection } from '@/hooks/useSupabaseConnection';
import type { Course } from "@/types";
import { getTrailName } from "@/constants/trails";

export interface RecommendedProduct {
  product_id: number;
  name: string;
  description: string | null;
  price: number;
  duration: string | null;
  main_photo_url: string | null;
  courses: Array<Course & { trail_name: string }>;
  nearest_trip: {
    trip_id: number;
    departure_date: string;
    status: string;
    booking_count: number;
    max_participants: number;
    min_participants: number;
  } | null;
}

// Type for the optimized database function response
interface OptimizedProductData {
  product_id: number;
  name: string;
  description: string | null;
  price: number;
  duration: string | null;
  main_photo_url: string | null;
  trip_id: number;
  departure_date: string;
  trip_status: string;
  max_participants: number;
  min_participants: number;
  booking_count: number;
  course_data: Array<{
    course_id: string;
    name: string;
    length: number | null;
    difficulty: string | null;
    start_point: string | null;
    end_point: string | null;
    short_desc: string | null;
    main_photo_url: string | null;
    trail_id: string;
  }>;
}

export const useRecommendedProducts = () => {
  const { supabase } = useSupabaseConnection();
  const query = useQuery({
    queryKey: ["recommended-products"],
    queryFn: async (): Promise<RecommendedProduct[]> => {
      console.log('🔍 추천 상품 조회 시작');

      try {
        // 간단한 HTTP 타임아웃 설정 (10초)
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000);

        const { data, error } = await supabase
          .rpc('get_recommended_products')
          .abortSignal(controller.signal);

        clearTimeout(timeoutId);

        if (error) {
          console.error('❌ 추천 상품 조회 실패:', error);
          throw error;
        }

        if (!data || data.length === 0) {
          console.log('ℹ️ 추천 가능한 상품이 없습니다');
          return [];
        }

        console.log(`✅ 추천 상품 ${data.length}개 조회 완료`);

        // 데이터 변환
        const transformedProducts: RecommendedProduct[] = data.map((product: OptimizedProductData) => {
          // 코스 데이터에 트레일 이름 추가
          const coursesWithTrailNames = Array.isArray(product.course_data) 
            ? product.course_data.map(course => ({
                ...course,
                trail_name: getTrailName(course.trail_id)
              }))
            : [];

          return {
            product_id: product.product_id,
            name: product.name,
            description: product.description,
            price: Number(product.price),
            duration: product.duration,
            main_photo_url: product.main_photo_url,
            courses: coursesWithTrailNames,
            nearest_trip: {
              trip_id: product.trip_id,
              departure_date: product.departure_date,
              status: product.trip_status,
              booking_count: Number(product.booking_count),
              max_participants: product.max_participants,
              min_participants: product.min_participants
            }
          };
        });

        return transformedProducts;
      } catch (error) {
        console.error('❌ 추천 상품 조회 오류:', error);
        throw error;
      }
    },
    staleTime: 5 * 60 * 1000, // 5분
    gcTime: 10 * 60 * 1000, // 10분
    retry: 2,
    refetchOnMount: true,
    // refetchOnWindowFocus: React Query 기본값 사용
    refetchOnReconnect: true,
  });
  
  return query;
};